import type { Plugin } from 'vue'
import type { Locale } from 'vue-i18n'
import messages from '@intlify/unplugin-vue-i18n/messages'
import PQueue from 'p-queue'
import { createI18n } from 'vue-i18n'

const datetimeFormats = {
  en: {
    'full': {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    },
    'date': {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    },
    'date-short': {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    },
    'datetime': {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    'datetime-short': {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    'time': {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
  },
  fr: {
    'full': {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    },
    'date': {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    },
    'date-short': {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    },
    'datetime': {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    'datetime-short': {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    'time': {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
  },
  nl: {
    'full': {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    },
    'date': {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    },
    'date-short': {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    },
    'datetime': {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    'datetime-short': {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    'time': {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
  },
} as const

const numberFormats = {
  en: {
    currency: {
      style: 'currency',
      currency: 'EUR' as 'EUR' | string,
      notation: 'standard',
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
    reporting: {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
  },
  fr: {
    currency: {
      style: 'currency',
      currency: 'EUR' as 'EUR' | string,
      notation: 'standard',
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
    reporting: {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
  },
  nl: {
    currency: {
      style: 'currency',
      currency: 'EUR' as 'EUR' | string,
      notation: 'standard',
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
    reporting: {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
  },
} as const

// Import i18n resources
// https://vitejs.dev/guide/features.html#glob-import
//
// Don't need this? Try vitesse-lite: https://github.com/antfu/vitesse-lite
const i18n = createI18n({
  legacy: false,
  locale: document?.querySelector('html')?.getAttribute('lang') ?? 'en',
  fallbackLocale: 'en',
  datetimeFormats,
  numberFormats,
  messages,
})

export const datetimeFormatter = computed(() => {
  const locale = i18n.global.locale.value

  return (format: keyof typeof datetimeFormats['en']) => new Intl.DateTimeFormat(locale, datetimeFormats[locale][format] ?? datetimeFormats.en[format])
})

const queue = new PQueue({ concurrency: 1 })

export async function ensureLanguageLoaded(lang: Locale) {
  if (lang === 'en' || i18n.global.availableLocales.includes(lang as any)) {
    return
  }

  await queue.add(async () => {
    if (i18n.global.availableLocales.includes(lang as any)) {
      return
    }

    const { data } = await axios.get(route('v2.translations.show', { locale: lang }))
    i18n.global.setLocaleMessage(lang, data)
  })
}

export async function setI18nLanguage(lang: Locale) {
  if (i18n.global.locale.value === lang) {
    return lang
  }

  await axios.post(route('v2.language.switch-locale', { locale: lang }))
  await ensureLanguageLoaded(lang)

  if (typeof document !== 'undefined') {
    document.querySelector('html')?.setAttribute('lang', lang)
  }

  i18n.global.locale.value = lang as any
  return lang
}

export function getLocaleIcon(lang: Locale): string {
  return matchDefault(lang, {
    en: 'i-flag-gb-4x3',
    fr: 'i-flag-fr-4x3',
    nl: 'i-flag-nl-4x3',
  }, '')
}

const install: Plugin & { global: typeof i18n.global } = function installI18n(app) {
  app.use(i18n)
}

install.global = i18n.global
export default install
